// [TODO] - Implement here
import React, { useMemo } from 'react';
import { Pressable } from 'react-native';
import { ConversionStatus } from '@alucio/aws-beacon-amplify/src/models';
import {
  Stack,
  DNAIcon,
  DNAText,
  DNABox,
  Rotate,
  Iffy,
  Popover,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAFileTypeChip, DNAPopover, DNAThumbnail, ThumbnailOverlayIcon } from 'src/components/DNA';
import { DocumentVersionORM } from 'src/types/types';
import { ERROR_MESSAGES } from 'src/screens/Publishers/Versioning/VersioningCriticalError';
import { PopoverProps } from '@alucio/lux-ui/src/components/visual/Popover/PopoverCore';
import { useAppSettings } from 'src/state/context/AppSettings';
import { getHiddenSlidePageStateByNumber } from 'src/utils/hiddenSlides';

export interface DNADocumentThumbnailProps {
  documentVersionORM: DocumentVersionORM,
  showProcessing?: boolean,
  unavailableContent?: boolean,
  variant?: 'default' | 'meeting',
  width: number,
  height: number,
  // [TODO-2126] - Instead of passing in an s3 key directly, we should instead pass in a page index
  //               since we have access to the DocumentVersionORM
  //             - Instead of passing in customThumbnail keys for offline/versioning use cases,
  //               this component should choose the thumbnailKey based on latestUsableVersion
  customThumbnailKey?: string,
  pageNumber?: number,
  hoverPreview?: boolean,
  onPress?: () => void,
}

export type ThumbnailProps = Exclude<DNADocumentThumbnailProps, 'showProcessing'>

const ErrorThumbnail: React.FC<ThumbnailProps> = (props) => {
  const { documentVersionORM } = props
  const errorCode = documentVersionORM.model.conversionErrorCode ?? ''
  const errorMessage  = ERROR_MESSAGES[errorCode]?.message ?? 'Error occurred. Please contact support.'

  return (
    <DNAPopover >
      <DNAPopover.Anchor>
        <DNAIcon.Styled
          status="danger"
          size="xl"
          name="warning-circle"
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">
          {errorMessage}
        </DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  )
}

const ProcessingThumbnail: React.FC<ThumbnailProps> = () => {
  return (
    <DNAPopover >
      <DNAPopover.Anchor>
        <Stack anchor="center">
          <Stack.Layer>
            <DNABox />
          </Stack.Layer>
          <Stack.Layer>
            <Rotate>
              <DNAIcon.Styled
                testID="thumbnail-optimizing-icon"
                status="tertiary"
                size="xl"
                name="arrow-clockwise"
              />
            </Rotate>
          </Stack.Layer>
        </Stack>
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">
          Optimizing
        </DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  )
}

const QueuedThumbnail: React.FC<ThumbnailProps> = () => {
  return (
    <DNAPopover >
      <DNAPopover.Anchor>
        <DNAIcon.Styled
          size="xl"
          name="image-fill"
          testID="no-content-thumbnail"
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">Queued</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  )
}

const ProcessedThumbnail: React.FC<ThumbnailProps> = (props) => {
  const { height, width, documentVersionORM, onPress, customThumbnailKey, pageNumber, variant } = props
  // [NOTE] - Fairly crude way, we might want to base this on the device mode
  //        - But this should work okay for now without prop drilling
  const chipSize = useMemo(
    () => width >= 100 ? 'md' : 'sm',
    [width],
  )
  const thumbnailPageState = useMemo(
    () => {
      const selectedThumbnail = documentVersionORM.model.selectedThumbnail ?? 1;
      return getHiddenSlidePageStateByNumber({
        pages: documentVersionORM.meta.allPages,
        pageNumber: pageNumber ?? (!customThumbnailKey ? selectedThumbnail : undefined),
      });
    },
    [customThumbnailKey, documentVersionORM.meta.allPages, documentVersionORM.model.selectedThumbnail, pageNumber],
  )

  const thumbnailStacks = (
    <Stack anchor="bottomRight">
      {/* Thumbnail Layer */}
      <Stack.Layer>
        <DNAThumbnail
          s3URL={customThumbnailKey ?? documentVersionORM.meta.assets.thumbnailKey}
          height={height}
          width={width}
          useLoadingIndicator
          variant={variant === 'meeting' ? DNAThumbnail.Variants.MEETING : undefined}
          isHidden={thumbnailPageState?.isHidden}
          sourceIsHidden={thumbnailPageState?.sourceIsHidden}
        />
      </Stack.Layer>

      {/* TODO: Consider reworking these stack.layers to be passed in and customizable rather than statically defined. */}
      {/* Document Type Layer */}
      <Stack.Layer anchor="bottomRight">
        <DNAFileTypeChip orm={documentVersionORM} variant="THUMBNAIL" size={chipSize}/>
      </Stack.Layer>
    </Stack>
  )

  /** Allow for optional custom interaction behavior */
  if (onPress) {
    return (
      <Pressable onPress={onPress}>
        {thumbnailStacks}
      </Pressable>
    )
  }

  /** Otherwise, return non-interactive version allowing for any parent container interactions to take effect */
  return (
    <>
      { thumbnailStacks }
    </>
  )
}

const processingMap = {
  [ConversionStatus.ERROR]: ErrorThumbnail,
  [ConversionStatus.PENDING]: QueuedThumbnail,
  [ConversionStatus.PROCESSING]: ProcessingThumbnail,
  [ConversionStatus.PROCESSED]: ProcessedThumbnail,
}

type DNADocumentThumbnailComponent = React.FC<DNADocumentThumbnailProps> & {
  Hover: typeof HoverPreview
}

export const DNADocumentThumbnail: DNADocumentThumbnailComponent = (props) => {
  const {
    unavailableContent,
    showProcessing,
    documentVersionORM,
    width,
    height,
    onPress,
    variant,
    customThumbnailKey,
    pageNumber,
  } = props

  const forwardProps = { width, height, documentVersionORM, onPress, customThumbnailKey, pageNumber, variant };

  const style = useMemo(
    () => ({
      // [TODO] - These are temporary since the thumbnails will be getting
      // reworked soon
      backgroundColor: '#F1F1F1',
      minWidth: width,
      minHeight: height,
    }),
    [width, height]
  )

  // Shows Document Thumbnail regardless of any new version being processed
  if (!showProcessing) {
    return (
      <React.Fragment>
        <Iffy is={!unavailableContent}>
          <ProcessedThumbnail {...forwardProps} />
        </Iffy>
        { /* NO CONTENT AVAILABLE THUMBNAIL */}
        <Iffy is={unavailableContent}>
          <Stack anchor="center">
            <Stack.Layer>
              <ProcessedThumbnail {...forwardProps} />
            </Stack.Layer>
            <Stack.Layer>
              <DNABox
                alignY="center"
                alignX="center"
                style={{ backgroundColor: colors['color-gray-transparent-40'], minHeight: height, width }}
              >
                <ThumbnailOverlayIcon variant="cloud" size={width > 200 ? 'xl' : 'md'}/>
              </DNABox>
            </Stack.Layer>
          </Stack>
        </Iffy>
      </React.Fragment>
    );
  }

  const ThumbnailToRender = documentVersionORM
    ? processingMap[documentVersionORM.model.conversionStatus]
    : QueuedThumbnail;

  return (
    <DNABox
      alignX="center"
      alignY="center"
      style={style}
    >
      <ThumbnailToRender {...forwardProps} />
    </DNABox>
  )
}

// [TODO] - This probably needs to be more dynamic
//        - There's a placement that will fit on which ever side has the most space
//          and the offset would need to dynamically reflect that as well
export const DEFAULT_POPOVER_PROPS: PopoverProps = {
  delay: [1000, 0],
  placement: 'right-start'
}

export const HOVER_PREVIEW_SIZES = {
  sm: {
    width: 120,
    height: 68,
  },
  md: {
    width: 160,
    height: 90,
  },
  lg: {
    width: 252,
    height: 140
  }
} as const

export type ThumbnailHoverProps = {
  popover?: PopoverProps,
  previewSize?: keyof typeof HOVER_PREVIEW_SIZES
}

export const useHoverContentOffset = (
  previewSize: NonNullable<ThumbnailHoverProps['previewSize']>,
  width: number
) => {  
  const previewOffset = useMemo<[number, number]>(
    () => {
      const dimensions = HOVER_PREVIEW_SIZES[previewSize]
      return [
        -(dimensions.height / 4),
        -(dimensions.width)
      ]
    },
    [previewSize, width]
  )

  return previewOffset
}

// [TODO] - This can probably be a generic wrapper / component
//        - All we need are the `ThumbnailHoverProps` (previewSize) and width
//        - It should be able to accept all components as long as those two props are available and just generically render the child anchor / preview component
//          with ...restProps
const HoverPreview: React.FC<React.PropsWithChildren<
  DNADocumentThumbnailProps &
  ThumbnailHoverProps
>> = (props) => {
  const { popover, previewSize = 'lg', ...restProps } = props
  const { deviceMode } = useAppSettings()

  const previewOffset = useHoverContentOffset(previewSize, restProps.width)

  if (deviceMode === 'tablet')
    return (
      <DNADocumentThumbnail {...restProps} />
    )
  
  return (
    <DNABox alignX='start'>
      <Popover
        {...DEFAULT_POPOVER_PROPS}
        {...popover}
        offset={previewOffset}
        type='preview'
        lazyMount
      >
        <Popover.Anchor>
          <DNADocumentThumbnail {...restProps} />
        </Popover.Anchor>
        <Popover.Content>
          <DNADocumentThumbnail
            {...restProps}
            width={HOVER_PREVIEW_SIZES[previewSize].width}
            height={HOVER_PREVIEW_SIZES[previewSize].height}
          />
        </Popover.Content>
      </Popover>
    </DNABox>
  )
} 

DNADocumentThumbnail.Hover = HoverPreview

export default DNADocumentThumbnail
